import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { FilterAltOff } from '@mui/icons-material'
import { GridLegacy as Grid, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'

import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import type { BudgetFilter } from '../FiltersPanelV2/types'
import CCMenuItemCard from '../ItemCard/MenuItemCard'
import CCMenuItemCardV2 from '../ItemCard/MenuItemCardV2'
import { CanteenCartItem } from '../MenuCartV2/types'
import { MenuSortOption } from '../MenuSort'
import { CartMappingOptionInput } from './cartMapping'

export const canteenMenuItemsMenuItemDataFragment = graphql(`
  fragment CanteenMenuItems_MenuItemDataFragment on CCMenuItem {
    id
    submenuId
    quantityRemaining
    vendorLocationId
    minimumPurchasablePrice
    liveVariation {
      _id
      priceCustomerFacing
      varType
      orderLimit
    }
    liveMenuItem {
      _id
      deleted
      deletedAt
      isActive
      isMenuItem
      name
      image
      dietaries
      allergens
      isCombo
      shortDescription
      createdAt
      variations {
        _id
        type
        priceCustomerFacing
        varType
      }
      subAllergens {
        nuts
        cereals
      }
      options {
        _id
        minQty
        maxQty
        qtyPerPerson
      }
      translations {
        key
        language
        value
      }
    }
  }
`)

export const canteenMenuItemsVendorDataFragment = graphql(`
  fragment CanteenMenuItems_VendorDataFragment on Vendor {
    id
    logo
    companyName
    currency
  }
`)

type MenuItemEntry = {
  item: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>
  vendor: FragmentType<typeof canteenMenuItemsVendorDataFragment>
}

const sortMenuItemEntries = (entries: MenuItemEntry[], sortOption: MenuSortOption) => {
  if (sortOption === 'itemType') return entries

  const decorated = entries.map((entry) => ({
    entry,
    // eslint-disable-next-line react-hooks/rules-of-hooks
    data: useFragment(canteenMenuItemsMenuItemDataFragment, entry.item),
  }))

  if (sortOption === 'priceLowToHigh' || sortOption === 'priceHighToLow') {
    const direction = sortOption === 'priceLowToHigh' ? 1 : -1
    decorated.sort(
      (a, b) =>
        ((a.data.liveVariation.priceCustomerFacing ?? 0) -
          (b.data.liveVariation.priceCustomerFacing ?? 0)) *
        direction,
    )
  } else if (sortOption === 'newest') {
    decorated.sort(
      (a, b) =>
        new Date(b.data.liveMenuItem.createdAt).getTime() -
        new Date(a.data.liveMenuItem.createdAt).getTime(),
    )
  }

  return decorated.map(({ entry }) => entry)
}

type Props = {
  vendorMenus: Array<{
    vendor: FragmentType<typeof canteenMenuItemsVendorDataFragment>
    mains: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>[]
    sides: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>[]
    drinks: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>[]
  }>
  cartItems: Array<CanteenCartItem>
  loading: boolean
  hidePrices: boolean
  useNewCcUi?: boolean
  budgetFilter: BudgetFilter
  balance: number
  availableSubsidy: number
  unlimitedSubsidy: boolean
  sortOption?: MenuSortOption
  isAddItemDisabled: (
    incomingId: string,
    orderLimit: number | null,
    quantityRemaining: number | null,
  ) => boolean
  handleSeeMore: (
    itemId: string,
    vendorId: string,
    itemData: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>,
  ) => void
  handleAddToCart: (
    itemData: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>,
    ccVariationId: string,
    quantity: number,
    options: Array<CartMappingOptionInput>,
    liveVariationQuantityData: { orderLimit: number | null; quantityRemaining: number | null },
  ) => void
}

const MenuItems: React.FC<Props> = ({
  vendorMenus = [],
  cartItems,
  loading,
  hidePrices,
  useNewCcUi = false,
  budgetFilter,
  balance,
  availableSubsidy,
  sortOption = 'itemType',
  unlimitedSubsidy,
  isAddItemDisabled,
  handleSeeMore,
  handleAddToCart,
}) => {
  const theme = useTheme()
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl'))
  const { t } = useTranslation()

  // MENU DATA
  const mains = sortMenuItemEntries(
    vendorMenus.flatMap((menu) => menu.mains.map((item) => ({ item, vendor: menu.vendor }))),
    sortOption,
  )
  const sides = sortMenuItemEntries(
    vendorMenus.flatMap((menu) => menu.sides.map((item) => ({ item, vendor: menu.vendor }))),
    sortOption,
  )
  const drinks = sortMenuItemEntries(
    vendorMenus.flatMap((menu) => menu.drinks.map((item) => ({ item, vendor: menu.vendor }))),
    sortOption,
  )

  const skeletonRenderArray = [1, 2, 3, 4, 5, 6]
  if (isLargeDesktop) skeletonRenderArray.push(7, 8, 9)

  return (
    <>
      <Grid spacing={1} width="100%" container alignItems="stretch">
        {loading ? (
          <>
            <Grid item xs={12} marginTop={1}>
              <Skeleton variant="rectangular" height="40px" width="100%" />
            </Grid>
            {skeletonRenderArray.map((num) => (
              <Grid key={num} md={6} lg={6} xl={4} xs={12} item>
                <Skeleton height="200px" width="100%" variant="rectangular" />
              </Grid>
            ))}
          </>
        ) : null}
        {!loading && mains.length === 0 && sides.length === 0 && drinks.length === 0 ? (
          <Stack
            paddingTop={2}
            direction="column"
            spacing={2}
            width="100%"
            alignItems="center"
            textAlign="center">
            <FilterAltOff color="disabled" sx={{ fontSize: 40 }} />
            <Typography variant="h5">{t('cc:oopsLooksLikeNoItems_title')}</Typography>
            <Typography variant="body1">{t('cc:oopsLooksLikeNoItems_subtitle')}</Typography>
          </Stack>
        ) : null}
        {[
          { items: mains, label: 'Mains' },
          { items: sides, label: 'Sides' },
          { items: drinks, label: 'Drinks' },
        ]
          .filter((itemType) => itemType.items.length)
          .map((itemType, sectionIndex) => (
            <Fragment key={itemType.label}>
              <Grid item xs={12} sx={sectionIndex > 0 ? { mt: 3 } : undefined}>
                <Typography variant="subtitle2">{itemType.label}</Typography>
              </Grid>
              {itemType.items.map((item) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const itemData = useFragment(canteenMenuItemsMenuItemDataFragment, item.item)
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const vendorData = useFragment(canteenMenuItemsVendorDataFragment, item.vendor)

                const individualItemData = {
                  submenuId: itemData.submenuId,
                  quantityRemaining: itemData.quantityRemaining,
                  liveVariation: itemData.liveVariation,
                  liveMenuItem: itemData.liveMenuItem,
                  minimumPurchasablePrice: itemData.minimumPurchasablePrice,
                }
                const { companyName, logo } = vendorData
                const disabled = isAddItemDisabled(
                  individualItemData.liveMenuItem._id,
                  individualItemData.liveVariation.orderLimit,
                  individualItemData.quantityRemaining,
                )

                // don't return the item card if the user can't afford it
                // under the selected budget filter
                if (!unlimitedSubsidy && budgetFilter) {
                  const price = individualItemData.minimumPurchasablePrice ?? 0
                  const allowance =
                    budgetFilter === 'subsidy'
                      ? Math.round((availableSubsidy ?? 0) * 100)
                      : Math.round((balance ?? 0) * 100) + Math.round((availableSubsidy ?? 0) * 100)
                  if (price > allowance) return null
                }

                return useNewCcUi ? (
                  <Grid md={6} lg={4} xl={3} xs={12} key={individualItemData.liveMenuItem._id} item>
                    <CCMenuItemCardV2
                      onSeeMoreClick={() =>
                        handleSeeMore(individualItemData.liveMenuItem._id, vendorData.id, item.item)
                      }
                      onAddToCart={() => {
                        handleAddToCart(item.item, individualItemData.liveVariation._id, 1, [], {
                          orderLimit: individualItemData.liveVariation.orderLimit ?? null,
                          quantityRemaining: individualItemData.quantityRemaining,
                        })
                      }}
                      vendorCompanyName={companyName}
                      vendorLogo={logo}
                      hidePrices={hidePrices}
                      itemData={individualItemData}
                      disableAddToCart={disabled}
                    />
                  </Grid>
                ) : (
                  <Grid md={6} lg={6} xl={4} xs={12} key={individualItemData.liveMenuItem._id} item>
                    <CCMenuItemCard
                      showHighlighted={
                        cartItems.findIndex(
                          (cartItem) => cartItem.id === individualItemData.liveMenuItem._id,
                        ) >= 0
                      }
                      onSeeMoreClick={() =>
                        handleSeeMore(individualItemData.liveMenuItem._id, vendorData.id, item.item)
                      }
                      onAddToCart={() => {
                        handleAddToCart(item.item, individualItemData.liveVariation._id, 1, [], {
                          orderLimit: individualItemData.liveVariation.orderLimit ?? null,
                          quantityRemaining: individualItemData.quantityRemaining,
                        })
                      }}
                      vendorCompanyName={companyName}
                      vendorLogo={logo}
                      hidePrices={hidePrices}
                      itemData={individualItemData}
                    />
                  </Grid>
                )
              })}
            </Fragment>
          ))}
      </Grid>
    </>
  )
}

export default MenuItems
